import React from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

export type JsonValueType = 'string' | 'number' | 'boolean' | 'null';

export type JsonEntry = {
  key: string;
  type: JsonValueType;
  value: string;
};

type Props = {
  label?: string;
  value: Record<string, any> | null | undefined;
  onChange: (next: Record<string, any>) => void;
  helperText?: string;
};

function objectToEntries(obj?: Record<string, any> | null): JsonEntry[] {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj).map(([key, value]) => {
    if (value === null) return { key, type: 'null', value: '' };
    if (typeof value === 'boolean') return { key, type: 'boolean', value: String(value) };
    if (typeof value === 'number') return { key, type: 'number', value: String(value) };
    return { key, type: 'string', value: String(value) };
  });
}

function entriesToObject(entries: JsonEntry[]): Record<string, any> {
  const obj: Record<string, any> = {};

  for (const entry of entries) {
    const key = String(entry.key || '').trim();
    if (!key) continue;

    switch (entry.type) {
      case 'number':
        obj[key] = entry.value === '' ? null : Number(entry.value);
        break;
      case 'boolean':
        obj[key] = entry.value === 'true';
        break;
      case 'null':
        obj[key] = null;
        break;
      case 'string':
      default:
        obj[key] = entry.value;
        break;
    }
  }

  return obj;
}

const JsonKeyValueEditor: React.FC<Props> = ({
                                               label = 'Configuration',
                                               value,
                                               onChange,
                                               helperText,
                                             }) => {
  const [entries, setEntries] = React.useState<JsonEntry[]>(() => objectToEntries(value));

  React.useEffect(() => {
    setEntries(objectToEntries(value));
  }, [value]);

  const updateEntries = (next: JsonEntry[]) => {
    setEntries(next);
    onChange(entriesToObject(next));
  };

  const addEntry = () => {
    updateEntries([
      ...entries,
      { key: '', type: 'string', value: '' },
    ]);
  };

  const updateEntry = (idx: number, patch: Partial<JsonEntry>) => {
    const next = [...entries];
    next[idx] = { ...next[idx], ...patch };
    updateEntries(next);
  };

  const removeEntry = (idx: number) => {
    updateEntries(entries.filter((_, i) => i !== idx));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2">{label}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addEntry}>
          Add Field
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No fields configured.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {entries.map((entry, idx) => (
            <Grid container spacing={1} key={idx}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Key"
                  value={entry.key}
                  onChange={(e) => updateEntry(idx, { key: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Type"
                  value={entry.type}
                  onChange={(e) =>
                    updateEntry(idx, {
                      type: e.target.value as JsonValueType,
                      value: e.target.value === 'boolean' ? 'false' : e.target.value === 'null' ? '' : entry.value,
                    })
                  }
                >
                  <MenuItem value="string">string</MenuItem>
                  <MenuItem value="number">number</MenuItem>
                  <MenuItem value="boolean">boolean</MenuItem>
                  <MenuItem value="null">null</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                {entry.type === 'boolean' ? (
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Value"
                    value={entry.value}
                    onChange={(e) => updateEntry(idx, { value: e.target.value })}
                  >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    value={entry.type === 'null' ? '' : entry.value}
                    disabled={entry.type === 'null'}
                    onChange={(e) => updateEntry(idx, { value: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={1} display="flex" alignItems="center" justifyContent="center">
                <IconButton color="error" onClick={() => removeEntry(idx)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Box>
      )}

      {helperText ? (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {helperText}
        </Typography>
      ) : null}
    </Paper>
  );
};

export default JsonKeyValueEditor;